import { useCallback, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { QUERY_PARAMS } from "@salesboard/contracts";
import { useT, type Translator } from "@/i18n";

/**
 * `hidden` means "no control in the filter panel" — **not** "invisible".
 *
 * A hidden filter still lives in the URL, still counts, still gets a chip, and still travels
 * to the API. Exactly one filter uses it now: `stalled`, which is the landing site for the
 * Stalled tile's click rather than a knob a reader reaches for. (`status` was the other, and
 * it is gone — every remaining filter *is* expressible in the panel, which is the whole point
 * of specs/filtering-multiselect.md.) What a hidden filter must never be is *silent* — an
 * active filter with no chip is a view lying about what it contains.
 */
type FilterVisibility = { hidden?: boolean };

export type FilterDef = (
  | { type: "search"; label?: string }
  | { type: "text"; label: string }
  | { type: "bool"; label: string; default?: boolean }
  | { type: "select"; label: string; options: { value: string; label: string }[]; default?: string }
  /**
   * A set of values, comma-joined in the URL (`opportunities:stage=a,b`) — the API contract's
   * own multi-value vocabulary. Rendered as a checkbox-list popover, and chipped **per value**
   * since 2026-08-26: one chip per key made dismissal all-or-nothing, so correcting one of
   * four chosen phases threw away the other three. The row does grow with the values, which
   * was the original objection — the answer is that the *key* is named once per group rather
   * than once per chip, so what grows is a list of values under one caption.
   */
  | { type: "multi"; label: string; options: { value: string; label: string }[] }
) &
  FilterVisibility;

export type FilterDefs = Record<string, FilterDef>;

/** One active filter, in the form both a chip and the no-result copy need. */
export type ActiveFilter = {
  /** The def key — what a chip's dismiss button clears. */
  key: string;
  /** The raw value, as it sits in the URL. */
  value: string;
  /** What a reader sees: `Sales manager: Petr Dvořák`, or `“rebrand”` for the search box. */
  label: string;
};

/**
 * One active filter and every value it holds — the shape the chip row renders.
 *
 * A group rather than a flat list of chips, because the key is stated once above its values.
 * `activeFilters` stays keyed per filter for the count and the copy: the Filter button's badge
 * says how many *filters* are on, and a reader who ticked four phases has set one filter, not
 * four.
 */
export type ActiveFilterGroup = {
  key: string;
  /** The filter's own name — `Stage`, `Sales manager`. Absent for the search box. */
  label: string;
  /** Every value it holds, each already in the words a reader recognises. */
  values: { value: string; label: string }[];
};

/**
 * The filter half of a screen's URL state, on its own.
 *
 * Extracted from `useServerTable` so the board and the list can wear the *same* filters
 * without the board also wearing a page and a sort it does not have. Both surfaces read and
 * write the same namespaced parameters, which is what makes the view toggle preserve the
 * filter set — the book calls consistent filtering the single most important shared
 * behaviour in the product, and one seam is how that stops being a promise and becomes
 * structurally true.
 */
export type FilterSet = {
  filters: Record<string, string>;
  /**
   * Every filter's value as a **set** — the raw parameter split on commas.
   *
   * Present for every def, not only the multi ones: a single-valued filter is a one-element
   * set and `stalled=true` is `["true"]`, which is what lets an analytics tile declare its
   * filters once and compare them by set-equality without knowing each key's arity.
   */
  values: Record<string, string[]>;
  setFilter: (key: string, value: string) => void;
  /** Write a whole set at once; an empty set deletes the parameter. */
  setValues: (key: string, values: string[]) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  /** Every active filter, one entry per key. Drives the count and the no-result copy. */
  activeFilters: ActiveFilter[];
  /** The same filters, opened out per value — what the chip row renders. */
  activeFilterGroups: ActiveFilterGroup[];
  /**
   * Drop one value from one filter, leaving its others alone.
   *
   * A multi loses that value and keeps the rest; anything single-valued is cleared, which for
   * it is the same thing.
   */
  removeValue: (key: string, value: string) => void;
  /** Labels alone, for the no-result state naming what is filtering. */
  activeFilterLabels: string[];
  /**
   * The active filters in the API's vocabulary — the search box travels as `q`, everything
   * else keeps its own name. Here rather than in each caller so the board and the list
   * cannot spell the same filter two ways.
   */
  apiParams: Record<string, string>;
};

/**
 * The value at which a filter is considered "off" — absent from the URL, uncounted, and
 * not sent to the API.
 *
 * Note for a `select` carrying a `default`: that default is now treated like any other
 * default and **omitted from the request**, where the previous table-only implementation
 * always sent it. No definition uses one today, so nothing changes; but a def that gains a
 * `default` must have the API defaulting to the same value, or the screen and the server
 * will disagree about what an unfiltered view means.
 */
function defaultValueOf(def: FilterDef): string {
  if (def.type === "bool") return String(def.default ?? false);
  return ("default" in def && def.default) || "";
}

/**
 * The words a reader would use, never the parameter name.
 *
 * `def.label` and the options' labels are already translated — they come from the screen's
 * filter definitions — so what the catalogue supplies here is the *punctuation of the phrase*:
 * how a label and its value are joined, and which quotation marks a search term wears
 * (`“term”` in English, `„term“` in Czech).
 */
function labelFor(t: Translator, def: FilterDef, value: string): string {
  if (def.type === "select") {
    return t("filter.valueChip", def.label, optionLabels(def.options, [value])[0] ?? value);
  }
  if (def.type === "multi") {
    // One chip per *key*, naming every value: `Fáze: Validation, Discovery`. A chip per
    // value would multiply the row by the number of options and make "which filters are on"
    // the thing the reader has to count.
    return t("filter.valueChip", def.label, optionLabels(def.options, splitValues(value)).join(", "));
  }
  if (def.type === "bool") return def.label;
  if (def.type === "search") return t("filter.searchChip", value);
  return t("filter.valueChip", def.label, value);
}

/**
 * The URL's comma-joined list, as a list.
 *
 * Exported because the multi control and the tile declarations read the same parameter and
 * must split it the same way — two spellings of "split on commas" is how a chip and a request
 * come to disagree about a trailing separator.
 */
export function splitValues(raw: string | undefined): string[] {
  return (raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Values as the words a reader recognises.
 *
 * Two unknowns, deliberately told apart — the options come from the API, so both happen:
 *
 * - **No options at all**: they have not landed yet. Every id would fall back to itself, and
 *   a chip reading `Sales manager: 00000000-4000-8000-…` is worse than one saying the value
 *   is still coming — the *key* is the half that answers "what is filtering", and it is
 *   already legible. One ellipsis for the set, not one per value: three of them joined by
 *   commas is noise about a list nobody can read yet.
 * - **Options present, this value absent**: a real unknown — a hand-edited parameter, or a
 *   manager removed since the link was shared. The raw value is the honest answer there, and
 *   the only clue anyone has to what the URL asked for.
 */
export function optionLabels(options: { value: string; label: string }[], values: string[]): string[] {
  if (options.length === 0) return values.length === 0 ? [] : ["…"];
  return values.map((value) => options.find((option) => option.value === value)?.label ?? value);
}

export function useFilterSet(
  namespace: string,
  defs: FilterDefs,
  options: {
    /**
     * Run inside the same URL mutation as a filter change — `useServerTable` uses it to
     * drop the page, since page 4 of the old result set is meaningless against the new one.
     * A hook rather than deleting `page` unconditionally here: the board has no page, and a
     * filter seam that quietly edits parameters it does not own is how a shared hook starts
     * surprising one of its callers.
     */
    onChange?: (params: URLSearchParams) => void;
  } = {},
): FilterSet {
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT();
  const { onChange } = options;

  // Held in a ref, not read in the callback body: `mutate` is memoised, and depending on
  // `location.state` would rebuild it — and every consumer of it — on each navigation.
  const location = useLocation();
  const stateRef = useRef(location.state);
  stateRef.current = location.state;

  const namespaced = useCallback((key: string) => `${namespace}:${key}`, [namespace]);

  const mutate = useCallback(
    (fn: (params: URLSearchParams) => void) => {
      // Live read, not the render-time snapshot: several setters may fire in one handler
      // and each must see the previous one's result. See useFilterParam for the bug this
      // prevents — clearing four filters used to clear only the last one.
      const params = new URLSearchParams(window.location.search);
      fn(params);
      onChange?.(params);
      // `state` is carried explicitly. `setSearchParams` replaces the history entry, and a
      // replace without state **erases** it — which on a detail route means losing the
      // background the modal is drawn over, so paging an embedded list would silently turn
      // a warm chain into a cold one and change what closing does.
      setSearchParams(params, { replace: true, state: stateRef.current });
    },
    [setSearchParams, onChange],
  );

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [key, def] of Object.entries(defs)) {
      out[key] = searchParams.get(namespaced(key)) ?? defaultValueOf(def);
    }
    return out;
  }, [searchParams, defs, namespaced]);

  const values = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [key, raw] of Object.entries(filters)) out[key] = splitValues(raw);
    return out;
  }, [filters]);

  const setFilter = useCallback(
    (key: string, value: string) => {
      const def = defs[key];
      const fallback = def ? defaultValueOf(def) : "";
      mutate((params) => {
        // A parameter at its default is absent, so a URL never carries noise and two
        // equivalent views produce the same link.
        if (!value || value === fallback) params.delete(namespaced(key));
        else params.set(namespaced(key), value);
      });
    },
    [defs, mutate, namespaced],
  );

  // Comma-joined, which is the API contract's multi-value spelling and therefore also the
  // URL's — one encoding, so `apiParams` can hand the parameter straight to the request.
  const setValues = useCallback(
    (key: string, next: string[]) => setFilter(key, next.join(",")),
    [setFilter],
  );

  const clearFilters = useCallback(() => {
    mutate((params) => {
      for (const key of Object.keys(defs)) params.delete(namespaced(key));
    });
  }, [defs, mutate, namespaced]);

  const activeFilters = useMemo(
    () =>
      Object.entries(filters).flatMap<ActiveFilter>(([key, value]) => {
        const def = defs[key];
        if (!def || value === "" || value === defaultValueOf(def)) return [];
        return [{ key, value, label: labelFor(t, def, value) }];
      }),
    [filters, defs, t],
  );

  /**
   * The same filters, opened out per value.
   *
   * The label of a *value* here, not of the filter — the group carries the filter's name once.
   * A search term and a bool have exactly one "value" each, and both keep the phrasing they
   * already had (`“rebrand”`, `Stalled only`), because neither reads as a value under a
   * caption: the term *is* the whole statement, and a bool's name *is* its on state.
   */
  const activeFilterGroups = useMemo(
    () =>
      Object.entries(filters).flatMap<ActiveFilterGroup>(([key, value]) => {
        const def = defs[key];
        if (!def || value === "" || value === defaultValueOf(def)) return [];

        if (def.type === "search" || def.type === "bool") {
          return [{ key, label: "", values: [{ value, label: labelFor(t, def, value) }] }];
        }

        const raw = def.type === "multi" ? splitValues(value) : [value];
        const options = "options" in def ? def.options : [];
        const labels = optionLabels(options, raw);
        return [
          {
            key,
            label: def.label,
            values: raw.map((one, index) => ({ value: one, label: labels[index] ?? one })),
          },
        ];
      }),
    [filters, defs, t],
  );

  const removeValue = useCallback(
    (key: string, value: string) => {
      const def = defs[key];
      if (def?.type !== "multi") {
        setFilter(key, "");
        return;
      }
      // Read from the live parameter rather than a render-time snapshot, for the same reason
      // every setter here does: two dismissals in one handler must each see the last one.
      const remaining = splitValues(filters[key]).filter((one) => one !== value);
      setValues(key, remaining);
    },
    [defs, filters, setFilter, setValues],
  );

  const apiParams = useMemo(() => {
    const out: Record<string, string> = {};
    for (const entry of activeFilters) {
      out[defs[entry.key]?.type === "search" ? QUERY_PARAMS.search : entry.key] = entry.value;
    }
    return out;
  }, [activeFilters, defs]);

  return {
    filters,
    values,
    setFilter,
    setValues,
    clearFilters,
    activeFilterCount: activeFilters.length,
    activeFilters,
    activeFilterGroups,
    removeValue,
    activeFilterLabels: activeFilters.map((entry) => entry.label),
    apiParams,
  };
}
